import { useEffect } from 'react';
import { redirect } from 'react-router-dom';
import Loadable from '~/components/LoadableErrorReload';
import EmailRegistrationRoute from '~/components/auth/EmailRegistrationRoute';
import ForgotPasswordRoute from '~/components/auth/ForgotPasswordRoute';
import LoginRoute from '~/components/auth/LoginRoute';
import SetPasswordRoute from '~/components/auth/SetPasswordRoute';
import { BASE_PATH } from '~/config';
import useAuth from '~/hooks/useAuth';
import AuthLayout from '~/layout/Auth';
import axios from '~/utils/axios';

const LoginMFA = Loadable(() => import('~/pages/auth/login/LoginMFA'));
const LoginWithEmail = Loadable(() => import('~/pages/auth/login/LoginWithEmail'));
const AuthCheckMail = Loadable(() => import('~/pages/auth/check-mail'));
const EmailVerificationRoute = Loadable(
  () => import('~/pages/auth/onboarding/routes/EmailVerificationRoute'),
);
const ErrorPage = Loadable(() => import('~/pages/maintenance/404'));

// this is a special case where you're logged into TRM
// but you try to go to the TRM dashboard from a different legacy user
const ForceLogout = () => {
  const { logout } = useAuth();

  useEffect(() => {
    const logoutUser = async () => {
      await logout();
      window.location.href = `${BASE_PATH}/`;
    };
    logoutUser();
  }, [logout]);

  return null;
};

const LoginRoutes = {
  path: '/',
  element: <AuthLayout />,
  children: [
    {
      path: '/',
      element: <LoginRoute />,
    },
    {
      path: 'login',
      element: <LoginRoute />,
    },
    {
      path: 'login/email',
      element: <LoginWithEmail />,
    },
    {
      path: 'login/mfa',
      element: <LoginMFA />,
    },
    {
      path: 'verify-email',
      element: <EmailVerificationRoute />,
    },
    {
      path: 'register',
      element: <EmailRegistrationRoute />,
    },
    {
      path: 'set-password',
      element: <SetPasswordRoute />,
    },
    {
      path: 'forgot-password',
      element: <ForgotPasswordRoute />,
    },
    {
      path: 'cognito-landing',
      loader: async () => {
        const params = new URLSearchParams(window.location.search);
        // The fragment keeps the token out of server logs and Referer headers. The query
        // fallback covers redirects issued by an API that predates the fragment.
        const token =
          new URLSearchParams(window.location.hash.slice(1)).get('token') ?? params.get('token');
        const trmPath = params.get('trmPath');

        const shouldNotRedirectToCognito = localStorage.getItem('shouldNotRedirectToCognito');
        if (!shouldNotRedirectToCognito) {
          localStorage.setItem('hasLoggedInViaCognito', 'true');
        }

        if (token) {
          axios.defaults.headers.common.Authorization = `Bearer ${token}`;

          // Clear token from URL
          params.delete('token');
          window.history.replaceState(
            {},
            '',
            `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`,
          );

          try {
            await axios.post('/me/set-last-ip');
          } catch (error) {
            console.error('Failed to set last IP:', error);
          }

          // Use redirect if trmPath exists
          if (trmPath) {
            const decodedTrmPath = decodeURIComponent(trmPath);
            // Parse the trmPath URL and remove token from its query params
            const trmUrl = new URL(decodedTrmPath, window.location.origin);
            trmUrl.searchParams.delete('token');
            return redirect(trmUrl.pathname + trmUrl.search); // Redirect using cleaned URL
          }

          return redirect('/dashboard/default');
        } else {
          return redirect('/login');
        }
      },
      element: null,
    },
    {
      path: 'legacy-landing',
      loader: async () => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        const trmPath = params.get('trmPath');

        if (token) {
          axios.defaults.headers.common.Authorization = `Bearer ${token}`;

          // Clear token from URL
          params.delete('token');
          window.history.replaceState(
            {},
            '',
            `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`,
          );

          try {
            await axios.post('/me/set-last-ip');
          } catch (error) {
            console.error('Failed to set last IP:', error);
          }

          // Use redirect if trmPath exists
          if (trmPath) {
            const decodedTrmPath = decodeURIComponent(trmPath);
            // Parse the trmPath URL and remove token from its query params
            const trmUrl = new URL(decodedTrmPath, window.location.origin);
            trmUrl.searchParams.delete('token');
            return redirect(trmUrl.pathname + trmUrl.search); // Redirect using cleaned URL
          }

          return redirect('/dashboard/default');
        } else {
          return redirect('/login');
        }
      },
      element: <ForceLogout />,
    },
    {
      path: 'check-mail',
      element: <AuthCheckMail />,
    },
    {
      path: '*',
      element: <ErrorPage />,
    },
  ],
};

export default LoginRoutes;
